import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import {
  User,
  Stethoscope,
  Building,
  Pill,
  TestTube,
  Heart,
  ChevronDown,
  X,
  Check,
  Loader2,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface UserRole {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  description: string;
  features: string[];
}

const userRoles: UserRole[] = [
  {
    id: 'patient',
    name: 'Patient',
    icon: User,
    description: 'Individual healthcare consumer',
    features: ['Health Records', 'Appointments', 'Medications', 'Test Results'],
  },
  {
    id: 'doctor',
    name: 'Doctor',
    icon: Stethoscope,
    description: 'Medical practitioner',
    features: ['Patient Management', 'Prescriptions', 'Consultations', 'Telemedicine'],
  },
  {
    id: 'hospital',
    name: 'Hospital',
    icon: Building,
    description: 'Healthcare facility',
    features: ['Staff Management', 'Patient Records', 'Facility Management', 'Analytics'],
  },
  {
    id: 'pharmacy',
    name: 'Pharmacy',
    icon: Pill,
    description: 'Medication dispensary',
    features: ['Prescription Verification', 'Inventory', 'Patient Consultation', 'Delivery'],
  },
  {
    id: 'lab',
    name: 'Testing Lab',
    icon: TestTube,
    description: 'Laboratory services',
    features: ['Test Processing', 'Results Management', 'Quality Control', 'Reporting'],
  },
  {
    id: 'health_services',
    name: 'Other Medical Org',
    icon: Heart,
    description: 'Other healthcare organizations',
    features: ['Service Management', 'Patient Care', 'Coordination', 'Compliance'],
  },
];

interface UserRoleSelectorProps {
  currentRole?: string;
  onRoleChange: (role: string) => void;
}

export default function UserRoleSelector({
  currentRole = 'patient',
  onRoleChange,
}: UserRoleSelectorProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const selectedRole = userRoles.find((role) => role.id === currentRole) || userRoles[0];

  // No auto-login: the mock session is established only when the developer
  // explicitly clicks "Not Logged In - Click to Login" below. The panel
  // itself only renders when VITE_TESTING_MODE=true (see useTestingMode).
  const loginTestUser = async () => {
    setIsLoading(true);
    try {
      // First try to register (in case user doesn't exist)
      await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: 'test@example.com',
          password: 'Test123!',
          name: 'Test Patient',
          userType: 'patient',
        }),
      });

      // Then login
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: 'test@example.com',
          password: 'Test123!',
        }),
      });

      if (res.ok) {
        setIsLoggedIn(true);
        toast({
          title: 'Logged In',
          description: 'Test user session active',
        });
      }
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      data-axe-skip="dev-testing-banner"
      className="fixed right-4 top-4 z-50 min-w-[300px] rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Testing Mode</h3>
        <div className="flex items-center gap-2">
          {isLoading ? (
            <Badge variant="secondary" className="flex items-center gap-1 text-xs">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading...
            </Badge>
          ) : isLoggedIn ? (
            <Badge variant="default" className="flex items-center gap-1 bg-green-600 text-xs">
              <Check className="h-3 w-3" />
              Logged In
            </Badge>
          ) : (
            <Badge variant="destructive" className="cursor-pointer text-xs" onClick={loginTestUser}>
              Not Logged In - Click to Login
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsVisible(false)}
            aria-label="Close testing mode banner"
            className="h-6 w-6 p-0 hover:bg-gray-200 dark:hover:bg-gray-700"
            data-testid="button-close-testing-banner"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <div className="mb-3">
        <label className="mb-2 block text-xs text-gray-500 dark:text-gray-400">
          Select User Type to Test:
        </label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between text-left" size="sm">
              <div className="flex items-center gap-2">
                <selectedRole.icon className="h-4 w-4" />
                <span>{selectedRole.name}</span>
              </div>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[280px]" align="end">
            {userRoles.map((role) => (
              <DropdownMenuItem
                key={role.id}
                onClick={() => onRoleChange(role.id)}
                className="flex cursor-pointer items-start gap-3 p-3"
              >
                <role.icon className="mt-0.5 h-4 w-4 text-medical-primary" />
                <div className="flex-1">
                  <div className="text-sm font-medium">{role.name}</div>
                  <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                    {role.description}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {role.features.slice(0, 2).map((feature, index) => (
                      <Badge key={index} variant="outline" className="px-1 py-0 text-xs">
                        {feature}
                      </Badge>
                    ))}
                    {role.features.length > 2 && (
                      <span className="text-xs text-gray-400">
                        +{role.features.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="border-t pt-2 text-xs text-gray-500 dark:text-gray-400">
        <div className="mb-1 font-medium">Current Features:</div>
        <div className="flex flex-wrap gap-1">
          {selectedRole.features.map((feature, index) => (
            <Badge key={index} variant="secondary" className="text-xs">
              {feature}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}
